import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import {
  ORGANISATION_FEATURES,
  type OrganisationFeature,
} from '@wundo/shared/organisationFeature.ts';
import type { Cohort } from '@/types/cohorts.ts';
import type {
  Organisation,
  OrganisationDetail,
  OrganisationSuggestion,
  OrganisationType,
} from '@/types/organisations.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import type { CalendarProxyStatus } from '@wundo/shared/calendarProxy.ts';

/*
 * Re-exported so the organisation pages and the shared picker have one import
 * for the record and the functions that fetch it. The shapes themselves live in
 * `types/organisations.ts`, beside the console's other API types.
 */
export type {
  Organisation,
  OrganisationDetail,
  OrganisationSuggestion,
  OrganisationType,
};

/** The three kinds, in the order the Vue console listed them. */
export const ORGANISATION_TYPES: readonly { value: OrganisationType; label: string }[] = [
  { value: 'internal', label: 'Internal' },
  { value: 'academy', label: 'Academy' },
  { value: 'business', label: 'Business' },
];

export function organisationTypeLabel(type: string): string {
  return ORGANISATION_TYPES.find((option) => option.value === type)?.label ?? type;
}

/**
 * What the form sends. Every field is optional because an edit sends only what
 * changed — the backend treats a missing key as "leave it alone".
 */
export type OrganisationInput = Partial<
  Pick<
    OrganisationDetail,
    | 'name'
    | 'type'
    | 'msteams_tenant_id'
    | 'default_cohort_id'
    | 'features'
    | 'internal_domains'
    | 'primary_domain'
    | 'calendar_proxy_url'
  >
>;

/** Paged and sorted by the server; without a sort it answers alphabetically. */
export interface OrganisationListParams {
  page: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function listOrganisations({
  page,
  sortBy,
  sortOrder,
}: OrganisationListParams): Promise<PaginatedResponse<OrganisationDetail>> {
  const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder ?? 'asc');
  }

  return apiFetchJson<PaginatedResponse<OrganisationDetail>>(`/organisations?${params}`);
}

/**
 * The organisations matching a query, for a picker. An empty query asks for the
 * first page, so opening the field already shows something to choose from.
 */
export async function searchOrganisations(
  query: string,
  signal?: AbortSignal,
): Promise<Organisation[]> {
  const suffix = query.trim() ? `&query=${encodeURIComponent(query.trim())}` : '';
  const { data } = await apiFetchJson<PaginatedResponse<Organisation>>(
    `/organisations?limit=${PAGE_SIZE}${suffix}`,
    { signal },
  );
  return data;
}

/* The most the endpoint hands out per page. */
const ALL_PAGE_LIMIT = 100;

/** Every organisation, narrowed to id, name and type. Follows the pages so none falls off the end. */
export async function listAllOrganisations(): Promise<Organisation[]> {
  const all: Organisation[] = [];

  for (let page = 1; ; page++) {
    const { data, meta } = await apiFetchJson<PaginatedResponse<Organisation>>(
      `/organisations?page=${page}&limit=${ALL_PAGE_LIMIT}`,
    );
    all.push(...data.map(({ id, name, type }) => ({ id, name, type })));

    if (page >= meta.totalPages) return all;
  }
}

export interface OrganisationGroup {
  label: string;
  organisations: Organisation[];
}

/** One group per type in `ORGANISATION_TYPES` order, so internal leads; names alphabetical inside. */
export function groupOrganisations(
  organisations: readonly Organisation[],
): OrganisationGroup[] {
  const order = (type: string) => {
    const index = ORGANISATION_TYPES.findIndex((option) => option.value === type);
    return index === -1 ? ORGANISATION_TYPES.length : index;
  };
  const types = [...new Set(organisations.map((organisation) => organisation.type))].sort(
    (a, b) => order(a) - order(b) || a.localeCompare(b),
  );

  return types.map((type) => ({
    label: organisationTypeLabel(type),
    organisations: organisations
      .filter((organisation) => organisation.type === type)
      .sort((a, b) => a.name.localeCompare(b.name)),
  }));
}

export function getOrganisation(id: string): Promise<OrganisationDetail> {
  return apiFetchJson<OrganisationDetail>(`/organisations/${id}`);
}

/** What the organisation's agendaproxy reports right now. Asked live, so it can be slow. */
export function getCalendarProxyStatus(id: string): Promise<CalendarProxyStatus> {
  return apiFetchJson<CalendarProxyStatus>(`/organisations/${id}/calendar-proxy`);
}

export function createOrganisation(
  input: OrganisationInput,
): Promise<OrganisationDetail> {
  return apiFetchJson<OrganisationDetail>('/organisations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Asks Microsoft about a customer's domain; an email address or website is fine too. */
export function suggestOrganisation(domain: string): Promise<OrganisationSuggestion> {
  return apiFetchJson<OrganisationSuggestion>(
    `/organisations/actions/suggest?domain=${encodeURIComponent(domain.trim())}`,
  );
}

export function updateOrganisation(
  id: string,
  input: OrganisationInput,
): Promise<OrganisationDetail> {
  return apiFetchJson<OrganisationDetail>(`/organisations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/* Mirrors the upload route's checks, so the dialog can refuse a file before sending it. */
export const ORGANISATION_LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_ORGANISATION_LOGO_BYTES = 1024 * 1024;

/** Multipart, field `file`; the content type is left to the browser for the boundary. */
export async function uploadOrganisationLogo(id: string, file: File): Promise<void> {
  const body = new FormData();
  body.append('file', file);

  await apiFetch(`/organisations/${id}/logo`, { method: 'PUT', body });
}

export async function removeOrganisationLogo(id: string): Promise<void> {
  await apiFetch(`/organisations/${id}/logo`, { method: 'DELETE' });
}

export type LogoFetchOutcome = 'stored' | 'not_found' | 'no_domain' | 'kept_upload';

/** Looks the logo up from the organisation's domain; takes a few seconds. */
export async function fetchOrganisationLogo(id: string): Promise<LogoFetchOutcome> {
  const { outcome } = await apiFetchJson<{ outcome: LogoFetchOutcome }>(
    `/organisations/${id}/actions/fetch-logo`,
    { method: 'POST' },
  );
  return outcome;
}

export async function deleteOrganisation(id: string): Promise<void> {
  await apiFetch(`/organisations/${id}`, { method: 'DELETE' });
}

/*
 * The two cohort reads an organisation needs: the one it defaults new Teams
 * installs into, and the list to pick that from. They live here rather than in
 * the cohorts domain because they are only ever asked from an organisation
 * page — the cohorts domain answers a different set of questions about the
 * same endpoint.
 */
export async function searchOrganisationCohorts(
  organisationId: string,
  query: string,
  signal?: AbortSignal,
): Promise<Cohort[]> {
  const suffix = query.trim() ? `&query=${encodeURIComponent(query.trim())}` : '';
  const { data } = await apiFetchJson<PaginatedResponse<Cohort>>(
    `/cohorts?organisation_id=${organisationId}${suffix}`,
    { signal },
  );
  return data;
}

/** The feature switches an organisation carries, in definition order. */
export const ORGANISATION_FEATURE_DEFINITIONS = Object.entries(ORGANISATION_FEATURES).map(
  ([key, value]) => ({
    key: key as OrganisationFeature,
    description: value.description,
    default: value.default,
  }),
);

/** A feature whose key is not what the console calls it. */
const FEATURE_LABELS: Partial<Record<OrganisationFeature, string>> = {
  eu_only: 'EU Only',
};

/** `disable_calendar` reads as "Disable Calendar". */
export function organisationFeatureLabel(key: string): string {
  return (
    FEATURE_LABELS[key as OrganisationFeature] ??
    key.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())
  );
}
